import React, { useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useHistory } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

import {
  selectNotifications,
  selectNotificationState,
} from '../../../redux/notification/notificationSelectors';
import { selectToken } from '../../../redux/user/userSelectors';

import {
  fetchNotificationsStart,
  markNotificationAsRead,
  markNotificationsSeenStart,
  clearNotifications,
} from '../../../redux/notification/notificationActions';

import UsersListSkeleton from '../../UsersList/UsersListSkeleton/UsersListSkeleton';
import Avatar from '../../Avatar/Avatar';
import Icon from '../../Icon/Icon';

const NotificationFeed = ({
  notifications,
  notificationState,
  fetchNotificationsStart,
  markNotificationAsRead,
  markNotificationsSeenStart,
  clearNotifications,
  token,
}) => {
  const history = useHistory();
  const sentinelRef = useRef(null);

  useEffect(() => {
    clearNotifications();
    if (!token) {
      return;
    }
    fetchNotificationsStart({ authToken: token });
  }, [token, fetchNotificationsStart, clearNotifications]);

  useEffect(() => {
    if (!token) {
      return;
    }
    if (notificationState.unseenCount > 0) {
      markNotificationsSeenStart(token);
    }
  }, [notificationState.unseenCount, token, markNotificationsSeenStart]);

  const handleLoadMore = useCallback(() => {
    if (
      !token ||
      notificationState.fetching ||
      !notificationState.hasMore ||
      !notificationState.cursor
    ) {
      return;
    }
    fetchNotificationsStart({
      authToken: token,
      cursor: notificationState.cursor,
      append: true,
    });
  }, [
    token,
    notificationState.fetching,
    notificationState.hasMore,
    notificationState.cursor,
    fetchNotificationsStart,
  ]);

  useEffect(() => {
    if (!notificationState.hasMore || notificationState.fetching) {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        handleLoadMore();
      }
    });
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => observer.disconnect();
  }, [notificationState.hasMore, notificationState.fetching, handleLoadMore]);

  const handleNavigate = (notification) => {
    if (!notification?.payload?.postId) {
      return;
    }
    if (!notification.isRead && token) {
      markNotificationAsRead({
        authToken: token,
        notificationId: notification.notificationId,
      });
    }
    history.push(`/post/${notification.payload.postId}`);
  };

  const renderPreviewMedia = (payload) => {
    if (!payload?.media?.url && !payload?.media?.thumbnailUrl) {
      return null;
    }
    const source = resolveMediaUrl(
      payload.media.thumbnailUrl || payload.media.url
    );
    const isVideo =
      payload.media.mediaType === 'video' ||
      /\.(mp4|mov|webm)$/i.test(source || '');

    return isVideo ? (
      <video
        className="notification-row__media"
        src={source}
        muted
        loop
        playsInline
      />
    ) : (
      <img
        className="notification-row__media"
        src={source}
        alt="notification preview"
      />
    );
  };

  const buildMessage = useCallback((notification) => {
    const payload = notification.payload || {};
    const actors = payload.actors || [];
    const primaryActor = actors[0];
    const totalActors = payload.totalActors || actors.length || 1;
    const others = totalActors - 1;
    const type = notification.notificationType;
    const isType = (...candidates) =>
      candidates.some(
        (candidate) =>
          candidate === type ||
          candidate === String(type) ||
          candidate === type?.toString()
      );

    if (isType('PostLiked', 'postLiked', 0)) {
        if (!primaryActor) {
          return 'Someone liked your post.';
        }
        return others > 0
          ? `${primaryActor.userName} and ${others} other${others > 1 ? 's' : ''
          } liked your post`
          : `${primaryActor.userName} liked your post`;
    }

    if (isType('PostCommented', 'postCommented', 1)) {
        if (!primaryActor) {
          return 'Someone commented on your post.';
        }
        return `${primaryActor.userName} commented on your post`;
    }

    if (isType('FollowedUserPublished', 'followedUserPublished', 2)) {
      return `${payload.followedUsername || actors[0]?.userName || 'Someone'
        } shared a new ${payload.media?.mediaType === 'video' ? 'video' : 'post'
      }`;
    }

    return 'You have a new notification.';
  }, []);

  const emptyState = (
    <div className="notification-empty">
      <Icon className="icon--larger" icon="heart-circle-outline" />
      <h2 className="heading-2 font-medium">No notifications yet</h2>
      <p className="body">Engage with your community to see updates here.</p>
    </div>
  );

  return (
    <div className="notification-feed">
      {notifications.length === 0 && notificationState.fetching ? (
        <UsersListSkeleton style={{ height: '10rem' }} />
      ) : notifications.length === 0 ? (
        emptyState
      ) : (
        <ul className="notification-feed__list">
          {notifications.map((notification) => {
            const payload = notification.payload || {};
            const actors = payload.actors || [];
            const primaryActor = actors[0];
            const timestamp = notification.createdAt
              ? formatDistanceToNow(new Date(notification.createdAt), {
                  addSuffix: true,
                })
              : '';

            return (
              <li
                key={notification.notificationId}
                className={`notification-row ${
                  notification.isRead ? '' : 'notification-row--unread'
                }`}
              >
                <button
                  type="button"
                  className="notification-row__content"
                  onClick={() => handleNavigate(notification)}
                >
                  <div className="notification-row__avatar">
                    {primaryActor ? (
                      <Avatar
                        imageSrc={primaryActor.avatar}
                        className="notification-row__avatar-image"
                      />
                    ) : (
                      <Icon icon="notifications-outline" />
                    )}
                  </div>
                  <div className="notification-row__body">
                    <p className="notification-row__headline">
                      {buildMessage(notification)}
                    </p>
                    {notification.notificationType === 'PostCommented' &&
                      payload.commentPreview && (
                        <p className="notification-row__excerpt">
                          {payload.commentPreview}
                        </p>
                      )}
                    <span className="notification-row__timestamp">
                      {timestamp}
                    </span>
                  </div>
                  {renderPreviewMedia(payload)}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <div ref={sentinelRef} />
      {notificationState.fetching && notifications.length > 0 && (
        <UsersListSkeleton style={{ height: '6rem' }} />
      )}
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  notifications: selectNotifications,
  notificationState: selectNotificationState,
  token: selectToken,
});

const mapDispatchToProps = (dispatch) => ({
  fetchNotificationsStart: (options) =>
    dispatch(fetchNotificationsStart(options)),
  markNotificationAsRead: (options) =>
    dispatch(markNotificationAsRead(options)),
  markNotificationsSeenStart: (authToken) =>
    dispatch(markNotificationsSeenStart(authToken)),
  clearNotifications: () => dispatch(clearNotifications()),
});

export default connect(mapStateToProps, mapDispatchToProps)(NotificationFeed);
const resolveMediaUrl = (url) => {
  if (!url) {
    return null;
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  const originBase =
    process.env.REACT_APP_API_ORIGIN ||
    (typeof window !== 'undefined' ? window.location.origin : '') ||
    '';
  const origin = originBase.replace(/\/+$/, '');
  return `${origin}/${url.replace(/^\/+/, '')}`;
};
